import type { AuthUser } from "@biosany/shared"
import { setAuthToken, setUnauthorizedHandler } from "@/lib/api"

const TOKEN_KEY = "biosany.token"
const USER_KEY = "biosany.user"

export function getToken(): string | null {
	if (typeof window === "undefined") return null
	return window.localStorage.getItem(TOKEN_KEY)
}

export function getUser(): AuthUser | null {
	if (typeof window === "undefined") return null
	const raw = window.localStorage.getItem(USER_KEY)
	if (!raw) return null
	try {
		return JSON.parse(raw) as AuthUser
	} catch {
		return null
	}
}

/** True if the JWT is missing, malformed, or past `exp` (client-side check only). */
export function isTokenExpired(token: string | null = getToken()): boolean {
	if (!token) return true
	const parts = token.split(".")
	if (parts.length < 2 || !parts[1]) return true
	try {
		const payload = JSON.parse(atob(parts[1])) as { exp?: unknown }
		if (typeof payload.exp !== "number") return true
		return payload.exp * 1000 <= Date.now()
	} catch {
		return true
	}
}

export function isAuthenticated(): boolean {
	const token = getToken()
	return Boolean(token) && !isTokenExpired(token)
}

export function saveAuth(token: string, user: AuthUser): void {
	window.localStorage.setItem(TOKEN_KEY, token)
	window.localStorage.setItem(USER_KEY, JSON.stringify(user))
	setAuthToken(token)
}

export function clearAuth(): void {
	window.localStorage.removeItem(TOKEN_KEY)
	window.localStorage.removeItem(USER_KEY)
	setAuthToken(null)
}

/** Restore the persisted token into the API client (call once on startup). */
export function restoreAuth(): void {
	const token = getToken()
	if (!token || isTokenExpired(token)) {
		clearAuth()
		return
	}
	setAuthToken(token)
}

/**
 * Clears the session and sends the browser to `/admin/login`.
 * Used when the API returns 401 or the JWT has expired.
 */
export function redirectToLogin(fromPath?: string): void {
	clearAuth()
	if (typeof window === "undefined") return

	const base = import.meta.env.BASE_URL || "/"
	const login = new URL("admin/login", `${window.location.origin}${base.endsWith("/") ? base : `${base}/`}`)
	if (fromPath && fromPath !== "/admin/login") {
		login.searchParams.set("from", fromPath)
	}

	const current = window.location.pathname.replace(/\/$/, "") || "/"
	const target = login.pathname.replace(/\/$/, "") || "/"
	if (current.endsWith("/admin/login") || current === target) return

	window.location.assign(login.pathname + login.search)
}

/** Wire API 401 responses to session clear + login redirect. Call once from RequireAuth. */
export function bindUnauthorizedRedirect(): void {
	setUnauthorizedHandler(() => {
		redirectToLogin(typeof window !== "undefined" ? window.location.pathname : undefined)
	})
}
